import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { switchMap, map, catchError, concatMap, withLatestFrom, tap } from 'rxjs/operators';
import { IStore } from 'src/app/core/root.store';
import { environment } from 'src/environments/environment';
import { AuthService } from '../../services/auth.service';
import { IAuthCommon, IAuthTable } from '../auth.interfaces';
import swal from 'sweetalert2';
import * as moment from 'moment';
import { UserLogin } from 'src/app/shared/services/roles.service';
import { FetchLotsAndUnits, SetIsLoggedInFalse, SetIsLoggedInTrue, SetIsSearcher, SetLoginCheckComplete } from 'src/app/core/store/ui/ui.actions';
import { getStrataPlanNumberFriendlyName } from 'src/app/core/store/ui/ui.selectors';
import { RemoveAllProfiles } from 'src/app/core/store/resident-profile/resident-profile.actions';
import {
    SearcherLogin
    , SearcherLoginSuccess
    , SearcherLoginFailure
    , SearcherAuthActionTypes
    , SearcherLoginSuccessRedirect
    , SearcherRefreshTokenFailure
    , SearcherDisplayError
    , SearcherLogout
    , SearcherRefreshToken
    , SearcherRefreshTokenSuccess
    , SearcherCheckLogin
    , SearcherCheckLoginFailure,
    SearcherSearchRequest,
    SearcherSearchRequestFailure,
    SearcherSearchRequestSuccess
} from './searcher-auth.actions';
import { selectDefaultSearcherAuthEntity } from './searcher-auth.selectors';
import { SearcherAuthService } from '../../services/searcher-auth.service';
import { ApproverLoginSuccess } from '../approver/approver-auth.actions';
import { LoginSuccess } from '../auth.actions';


@Injectable()
export class SearcherAuthEffects {
    
    login$ = createEffect(() => this.actions$.pipe(
        ofType<SearcherLogin>(SearcherAuthActionTypes.SEARCHER_LOGIN),
        switchMap((action) => {
            return this.searcherAuthService.searcherLogin(action.payload).pipe(
                map((authResponse: IAuthCommon) => {
                    localStorage.setItem(
                        environment.auth.token,
                        authResponse.accessToken
                    );
                    localStorage.setItem(
                        environment.auth.token_expiry,
                        authResponse.expiresIn.toString()
                    );
                    localStorage.setItem(
                        environment.auth.refresh_token,
                        authResponse.refreshToken
                    );

                    let authState: IAuthTable = {
                        entities: {
                            '5334f7c7-16e6-4714-9fce-8a2a6e60e79c': {
                                accessToken: authResponse.accessToken,
                                expiresIn: authResponse.expiresIn,
                                refreshToken: authResponse.refreshToken,
                                loggedIn: true,
                            },
                        },
                        ids: ['5334f7c7-16e6-4714-9fce-8a2a6e60e79c'],
                    };
                    localStorage.setItem(environment.ui.logged_user_name, action.payload.userName);
                    localStorage.setItem(environment.auth.user_login, UserLogin.Searcher);
                    return new SearcherLoginSuccess(authState);
                }),
                catchError((error) => {
                    return of(new SearcherLoginFailure(error));
                })
            );
        })
    ));

    
    searcherLoginSuccess$ = createEffect(() => this.actions$.pipe(
        ofType<SearcherLoginSuccess>(SearcherAuthActionTypes.SEARCHER_LOGIN_SUCCESS),
        withLatestFrom(
            this.store$.select(
                selectDefaultSearcherAuthEntity('5334f7c7-16e6-4714-9fce-8a2a6e60e79c')
            )
        ),
        map(([action, authUser]) => {
            return authUser;
        }), withLatestFrom(
            this.store$.select(getStrataPlanNumberFriendlyName)
        ), map(([action, authUser]) => {
            return authUser;
        }),
        concatMap((strataPlanNumber) => [
            new SetLoginCheckComplete(),
            new SetIsLoggedInTrue(),
            new SetIsSearcher(),
            new SearcherLoginSuccessRedirect(),
            new FetchLotsAndUnits(strataPlanNumber),
            new RemoveAllProfiles()
        ])
    ));

    
    loginRedirect$ = createEffect(() => this.actions$.pipe(
        ofType<SearcherLoginSuccessRedirect>(SearcherAuthActionTypes.SEARCHER_LOGIN_SUCCESS_REDIRECT),
        tap(() => {
            if (localStorage.getItem(environment.auth.user_login) === UserLogin.Searcher) {
                this.router.navigate([environment.auth.auth_success_url_searcher]);
            }
        })
    ), { dispatch: false });

    
    loginError$ = createEffect(() => this.actions$.pipe(
        ofType<SearcherLoginFailure>(SearcherAuthActionTypes.SEARCHER_LOGIN_FAILURE),
        tap((err: any) => { })
    ), { dispatch: false });

    
    refreshTokenError$ = createEffect(() => this.actions$.pipe(
        ofType<SearcherRefreshTokenFailure>(SearcherAuthActionTypes.SEARCHER_REFRESH_TOKEN_FAILURE),
        concatMap((err: any) => [
            new SearcherDisplayError('Your session has timed out. Please login again.'),
            new SearcherLogout(),
        ])
    ));


    
    searcherRefreshToken$ = createEffect(() => this.actions$.pipe(
        ofType<SearcherRefreshToken>(SearcherAuthActionTypes.SEARCHER_REFRESH_TOKEN),
        withLatestFrom(
            this.store$.select(
                selectDefaultSearcherAuthEntity('5334f7c7-16e6-4714-9fce-8a2a6e60e79c')
            )
        ),
        switchMap(([, authUser]) => {
            if (!authUser) {
                return of(new SearcherLogout());
            }

            return this.authService.refreshToken(authUser.refreshToken).pipe(
                map((authResponse: IAuthCommon) => {
                    localStorage.setItem(
                        environment.auth.token,
                        authResponse.accessToken
                    );
                    localStorage.setItem(
                        environment.auth.token_expiry,
                        authResponse.expiresIn.toString()
                    );
                    localStorage.setItem(
                        environment.auth.refresh_token,
                        authResponse.refreshToken
                    );

                    let authState: IAuthTable = {
                        entities: {
                            '5334f7c7-16e6-4714-9fce-8a2a6e60e79c': {
                                accessToken: authResponse.accessToken,
                                expiresIn: authResponse.expiresIn,
                                refreshToken: authResponse.refreshToken,
                                loggedIn: true,
                            },
                        },
                        ids: ['5334f7c7-16e6-4714-9fce-8a2a6e60e79c'],
                    };
                    return new SearcherRefreshTokenSuccess(authState);
                }),
                catchError((error) => {
                    return of(new SearcherRefreshTokenFailure(error));
                })
            );
        }),
        concatMap(() => [
            new SetIsSearcher(),
        ])
    ));

    
    searcherLogout$ = createEffect(() => this.actions$.pipe(
        ofType<SearcherLogout>(SearcherAuthActionTypes.SEARCHER_LOGOUT),
        withLatestFrom(this.store$.select(getStrataPlanNumberFriendlyName)),
        map(([action, friendlyName]) => {
            localStorage.removeItem(environment.auth.token);
            localStorage.removeItem(environment.auth.token_expiry);
            localStorage.removeItem(environment.auth.refresh_token);
            localStorage.removeItem(environment.auth.user_login);
            localStorage.removeItem(environment.ui.logged_user_name);
            return new SetIsLoggedInFalse();
        })
    ));


    
    checkLogin$ = createEffect(() => this.actions$.pipe(
        ofType<SearcherCheckLogin>(SearcherAuthActionTypes.SEARCHER_CHECK_LOGIN),
        map((action) => {
            let accessToken = localStorage.getItem(environment.auth.token);
            let userLogin = localStorage.getItem(environment.auth.user_login);
            if (accessToken) {
                let expiresIn = +localStorage.getItem(environment.auth.token_expiry);
                let refreshToken = localStorage.getItem(environment.auth.refresh_token);
                let authState: IAuthTable = {
                    entities: {
                        '5334f7c7-16e6-4714-9fce-8a2a6e60e79c': {
                            accessToken,
                            expiresIn,
                            refreshToken,
                            loggedIn: true,
                        },
                    },
                    ids: ['5334f7c7-16e6-4714-9fce-8a2a6e60e79c'],
                };
                const currentUtcUnixTime = moment().utc().unix();
                if (expiresIn > currentUtcUnixTime) {
                    if (localStorage.getItem(environment.auth.user_login) === UserLogin.Approver) {
                        return new ApproverLoginSuccess(authState);
                    }
                    if (localStorage.getItem(environment.auth.user_login) === UserLogin.Searcher) {
                        return new SearcherLoginSuccess(authState);
                    }
                    else {
                        return new LoginSuccess(authState);
                    }
                } else {
                    return new SearcherCheckLoginFailure();
                }
            } else {
                return new SetLoginCheckComplete();
            }
        })
    ));

    
    signUp$ = createEffect(() => this.actions$.pipe(
        ofType<SearcherSearchRequest>(SearcherAuthActionTypes.SEARCH_REQUEST),
        switchMap((action) => {
            return this.searcherAuthService.searchRequest(action.payload).pipe(
                map((res) => new SearcherSearchRequestSuccess(res)),
                catchError((err) => of(new SearcherSearchRequestFailure(err)))
            );
        })
    ));

    
    signUpSuccess$ = createEffect(() => this.actions$.pipe(
        ofType<SearcherSearchRequestSuccess>(SearcherAuthActionTypes.SEARCH_REQUEST_SUCCESS),
        tap()
    ), { dispatch: false });

    
    signUpFailure$ = createEffect(() => this.actions$.pipe(
        ofType<SearcherSearchRequestFailure>(SearcherAuthActionTypes.SEARCH_REQUEST_FAILURE),
        tap((res) => console.log(res))
    ), { dispatch: false });

    
    checkLoginFailure$ = createEffect(() => this.actions$.pipe(
        ofType<SearcherCheckLoginFailure>(SearcherAuthActionTypes.SEARCHER_CHECK_LOGIN_FAILURE),
        concatMap(() => [
            new SetLoginCheckComplete(),
            new SetIsLoggedInFalse(),
            new SearcherLogout(),
        ])
    ));

    
    displayError$ = createEffect(() => this.actions$.pipe(
        ofType<SearcherDisplayError>(SearcherAuthActionTypes.SEARCHER_DISPLAY_ERROR),
        tap((action) => {
            swal.fire({
                position: 'center',
                icon: 'info',
                title: action.payload,
                showConfirmButton: true,
                timer: 5000,
            });
        })
    ), { dispatch: false });


    constructor(
        private actions$: Actions,
        private router: Router,
        private searcherAuthService: SearcherAuthService,
        private authService: AuthService,
        private store$: Store<IStore>
    ) { }
}

